import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Sparkles, BookOpen, Brain, Zap, ArrowRight, Compass } from 'lucide-react';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(229, 9, 20, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background-image: 
    linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.95)), 
    url('/images/guest_guide_bg.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #ffffff;
  padding: 60px 20px 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
`;

const HeroSection = styled.div`
  text-align: center;
  max-width: 800px;
  margin-bottom: 60px;
  animation: ${fadeIn} 1s ease-out;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(229, 9, 20, 0.15);
  color: #ff4d4d;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 24px;
  border: 1px solid rgba(229, 9, 20, 0.3);
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 24px;
  line-height: 1.2;
  background: linear-gradient(135deg, #fff 0%, #a0a0a0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #a0a0a0;
  line-height: 1.6;
  margin-bottom: 40px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 80px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 40px 30px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: ${fadeIn} 1s ease-out ${(props) => props.delay}s backwards;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${(props) => props.color}, transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-10px) scale(1.02);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    
    &::before {
      opacity: 1;
    }

    .icon-wrapper {
      transform: scale(1.1) rotate(5deg);
    }
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: ${(props) => props.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: ${(props) => props.color};
  transition: transform 0.3s ease;
  animation: ${float} 4s ease-in-out infinite ${(props) => props.delay || 0}s;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #ffffff;
`;

const CardDesc = styled.p`
  font-size: 1rem;
  color: #888;
  line-height: 1.6;
`;

const CTAContainer = styled.div`
  text-align: center;
  animation: ${fadeIn} 1s ease-out 0.8s backwards;
  position: relative;
  z-index: 10;
`;

const CTAButton = styled.button`
  background: #e50914;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 20px 48px;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  animation: ${pulse} 2s infinite;

  &:hover {
    background: #f40612;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(229, 9, 20, 0.3);
  }

  svg {
    transition: transform 0.3s;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

function GuestGuidePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStart = () => {
    navigate('/feed');
  };

  const features = [
    {
      id: 1,
      title: "마법 같은 AI 피드",
      desc: "관심 있는 최신 영어 뉴스를 읽다가 모르는 단어를 누르기만 하세요. AI가 문맥에 딱 맞는 뜻을 즉시 분석해 줍니다.",
      icon: <Zap size={28} />,
      color: "#f59e0b",
      bg: "rgba(245, 158, 11, 0.15)",
      delay: 0.2,
      link: "/feed"
    },
    {
      id: 2,
      title: "나만의 똑똑한 단어장",
      desc: "뉴스에서 누른 단어들은 자동으로 나만의 단어장에 저장됩니다. 문장과 함께 저장되어 실전 활용도를 높여줍니다.",
      icon: <BookOpen size={28} />,
      color: "#3b82f6",
      bg: "rgba(59, 130, 246, 0.15)",
      delay: 0.4,
      link: "/word-history"
    },
    {
      id: 3,
      title: "망각 곡선 스마트 학습",
      desc: "인간의 뇌과학을 적용한 AI 퀴즈가 당신이 단어를 잊을 만할 때쯤 기막히게 찾아와 완벽한 암기를 돕습니다.",
      icon: <Brain size={28} />,
      color: "#10b981",
      bg: "rgba(16, 185, 129, 0.15)",
    },
    {
      id: 4,
      title: "맞춤형 AI 학습 자료",
      desc: "당신의 관심사와 수준에 딱 맞는 맞춤형 영어 지문을 AI가 즉시 생성해 줍니다. 매일 흥미로운 주제로 학습해 보세요.",
      icon: <Compass size={28} />,
      color: "#8b5cf6",
      bg: "rgba(139, 92, 246, 0.15)",
      delay: 0.8,
      link: "/ai-study"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <Badge>
          <Sparkles size={16} />
          <span>게스트 체험 모드 활성화</span>
        </Badge>
        <Title>누름보카의 혁신적인<br/>세계를 경험해보세요</Title>
        <Subtitle>
          영어 학습의 새로운 패러다임. 억지로 외우는 고통은 끝났습니다.<br/>
          당신의 호기심이 곧 실력이 되는 마법 같은 여정을 지금 시작합니다.
        </Subtitle>
      </HeroSection>

      <Grid>
        {features.map((feature) => (
          <Card key={feature.id} delay={feature.delay} color={feature.color} onClick={() => navigate(feature.link)}>
            <IconWrapper className="icon-wrapper" bg={feature.bg} color={feature.color} delay={feature.delay}>
              {feature.icon}
            </IconWrapper>
            <CardTitle>{feature.title}</CardTitle>
            <CardDesc>{feature.desc}</CardDesc>
          </Card>
        ))}
      </Grid>

      <CTAContainer>
        <CTAButton onClick={handleStart}>
          전체 서비스 시작하기
          <ArrowRight size={24} />
        </CTAButton>
        <p style={{ marginTop: '20px', color: '#666', fontSize: '0.9rem' }}>
          * 모든 기능은 게스트 계정으로 제약 없이 체험 가능합니다.
        </p>
      </CTAContainer>
    </PageContainer>
  );
}

export default GuestGuidePage;
